import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './hooks/use-toast';
import { TooltipProvider } from './components/ui/tooltip';
import { store } from './store';
import { useAnalytics } from './hooks/useAnalytics';
import { AgentAuthProvider } from './contexts/AgentAuthContext';
import Home from './pages/Home';
import NotFound from './pages/not-found';
import BlogPost from './components/BlogPost';
import BlogList from './components/BlogList';
import Webinar from './pages/Webinar';
import HealthCheck from './pages/HealthCheck';
import SubmitTestimonial from './pages/SubmitTestimonial';
import Licensing from './pages/Licensing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RegulatoryDisclaimer from './pages/RegulatoryDisclaimer';
import AdminTestimonials from './pages/AdminTestimonials';
import AgentsLogin from './pages/agents/AgentsLogin';
import AgentsLayout from './pages/agents/AgentsLayout';
import AgentsDashboard from './pages/agents/AgentsDashboard';
import AgentsGuard from './pages/agents/AgentsGuard';
import PlaceholderTool from './pages/agents/tools/PlaceholderTool';
import FinancialNeedAnalysis from './pages/agents/tools/FinancialNeedAnalysis';
import FNADashboard from './pages/agents/tools/FNADashboard';
import FNAReportPage from './pages/agents/tools/FNAReportPage';
import InvestmentComparison from './pages/agents/tools/InvestmentComparison';
import IMOCompensation from './pages/agents/tools/IMOCompensation';
import AdminAgentUsage from './pages/agents/AdminAgentUsage';
import './index.css';

import AdminLayout from './pages/admin/AdminLayout';

function AppContent() {
  useAnalytics();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/webinar" element={<Webinar />} />
      <Route path="/licensing" element={<Licensing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<RegulatoryDisclaimer />} />
      <Route path="/health" element={<HealthCheck />} />
      <Route path="/submit-testimonial" element={<SubmitTestimonial />} />
      <Route path="/admin/*" element={<AdminLayout />} />
      <Route path="/agents/*" element={<AgentAuthWrapper />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AgentAuthWrapper() {
  return (
    <AgentAuthProvider>
      <Routes>
        <Route path="login" element={<AgentsLogin />} />
        <Route element={<AgentsGuard><AgentsLayout /></AgentsGuard>}>
          <Route index element={<AgentsDashboard />} />
          <Route path="placeholder" element={<PlaceholderTool />} />
          <Route path="financial-need-analysis" element={<FNADashboard />} />
          <Route path="financial-need-analysis/new" element={<FinancialNeedAnalysis />} />
          <Route path="financial-need-analysis/edit/:reportId" element={<FinancialNeedAnalysis />} />
          <Route path="financial-need-analysis/report/:reportId" element={<FNAReportPage />} />
          <Route path="investment-comparison" element={<InvestmentComparison />} />
          <Route path="imo-compensation" element={<IMOCompensation />} />
        </Route>
        <Route path="*" element={<Navigate to="/agents" replace />} />
      </Routes>
    </AgentAuthProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <Router basename="/">
          <TooltipProvider>
            <Toaster />
            <AppContent />
          </TooltipProvider>
        </Router>
      </HelmetProvider>
    </Provider>
  );
}

export default App;
